import axios from "axios";

import {
  AddScore,
  Leaderboard,
  LeaderboardRow,
  Cercle,
  LoginRequest,
  User,
  UpdateUser,
  RemoveScore,
} from "../services/types";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-type": "application/json",
  },
});

apiClient.interceptors.request.use((config) => {
  if (!config?.headers) {
    throw new Error(
      `Expected 'config' and 'config.headers' not to be undefined`
    );
  }
  const token = localStorage.getItem("token");
  if (token !== null) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const leaderboardById = async (id: number) => {
  const response = await apiClient.get<LeaderboardRow[]>(`/leaderboard/${id}`);
  return response.data;
};

const addScore = async (newScore: AddScore) => {
  const response = await apiClient.post<any>("/addScore", {
    ...newScore,
  });
  return response.data;
};

const findAllLeaderboards = async () => {
  const response = await apiClient.get<Leaderboard[]>(`/leaderboards`);
  return response.data;
};

const createLeaderboard = async (leaderboard: Leaderboard) => {
  const response = await apiClient.post<any>("/leaderboard", {
    ...leaderboard,
  });
  return response.data;
};

const findAllCerles = async () => {
  const response = await apiClient.get<Cercle[]>(`/cercles`);
  return response.data;
};

const createCercle = async (cercle: Cercle) => {
  const response = await apiClient.post<any>("/cercle", {
    ...cercle,
  });
  return response.data;
};

const updateCercle = async (id: number, cercle: Cercle) => {
  const response = await apiClient.put<any>(`/cercle/${id}`, {
    ...cercle,
  });
  return response.data;
};

const syncCercles = async () => {
  const response = await apiClient.get<any>(`/cercles/sync`);
  return response.data;
};

const deleteCercleById = async (id: number) => {
  const response = await apiClient.delete<any>(`/cercle/${id}`);
  return response.data;
};

const deleteLeaderboardById = async (id: number) => {
  const response = await apiClient.delete<any>(`/leaderboard/${id}`);
  return response.data;
};

const deleteCercleFromLeaderboard = async (removeScore: RemoveScore) => {
  const response = await apiClient.delete<any>(
    `/deleteScore/${removeScore.leaderboard_id}/${removeScore.cercle_id}`
  );
  return response.data;
};

const uploadFile = async (file: File) => {
  let formData = new FormData();
  formData.append("upload_file", file);
  const response = await apiClient.post("/upload", formData);
  return response.data;
};

const authLogin = async (login: LoginRequest) => {
  const formData = new FormData();
  formData.append("username", login.username);
  formData.append("password", login.password);
  const response = await apiClient.post<any>("/auth/jwt/login", formData);

  return response.data;
};

const findAllUsers = async () => {
  const response = await apiClient.get<User[]>("/users");
  return response.data;
};

const updateUser = async (id: string, user: User) => {
  const response = await apiClient.patch<UpdateUser[]>(`/users/${id}`, {
    ...user,
  });
  return response.data;
};

const LeaderboardService = {
  leaderboardById,
  deleteCercleById,
  updateCercle,
  createCercle,
  createLeaderboard,
  deleteLeaderboardById,
  findAllCerles,
  uploadFile,
  findAllLeaderboards,
  addScore,
  authLogin,
  findAllUsers,
  syncCercles,
  updateUser,
  deleteCercleFromLeaderboard,
  BASE_URL,
};

export default LeaderboardService;
